
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getSupabase } from '../lib/supabase';
import { Property, Agent, Review } from '../types';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { Bed, Bath, Square, MapPin, Phone, Mail, MessageCircle, ArrowLeft, Download, Star, CheckCircle, ShieldCheck, AlertCircle, X, Send, Loader2, Sparkles, ChevronLeft, ChevronRight, Grid, DollarSign, Wallet, RefreshCw, Layers } from 'lucide-react';

const PropertyDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  
  // Gallery State
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showFullGallery, setShowFullGallery] = useState(false);

  // Review State
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [verificationStep, setVerificationStep] = useState<'form' | 'verifying' | 'success'>('form');
  const [reviewForm, setReviewForm] = useState({ name: '', email: '', rating: 5, comment: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [verificationProgress, setVerificationProgress] = useState(0);

  useEffect(() => {
    const fetchProperty = async () => {
      const supabase = getSupabase();
      if (!supabase) return;
      try {
        const { data: propData } = await supabase.from('properties').select('*').eq('id', id).single();
        if (propData) {
          const mappedProp: Property = { 
            ...propData, 
            imageUrl: propData.image_url, 
            images: propData.images || [propData.image_url],
            projectName: propData.project_name,
            originalPrice: propData.original_price,
            isDistress: propData.is_distress,
            propertyType: propData.property_type,
            sizeSqm: propData.size_sqm,
            bedType: propData.bed_type,
            bathType: propData.bath_type,
            studyRoom: propData.study_room,
            rentalFreq: propData.rental_freq,
            cheques: propData.cheques,
            referenceId: propData.reference_id
          };
          setProperty(mappedProp);
          if (mappedProp.agentId) {
             const { data: agentData } = await supabase.from('agents').select('*').eq('id', mappedProp.agentId).single();
             if (agentData) setAgent({ ...agentData, photoUrl: agentData.photo_url || agentData.photoUrl });
          }
        }
        const { data: reviewData } = await supabase.from('reviews').select('*').eq('property_id', id).eq('status', 'approved');
        if (reviewData) setReviews(reviewData as any);
      } catch (e) {}
      setLoading(false);
    };
    if (id) fetchProperty();
  }, [id]);

  const verifyEmailRealTime = async (email: string): Promise<boolean> => {
    setVerificationStep('verifying');
    setVerificationProgress(10);
    await new Promise(r => setTimeout(r, 600)); setVerificationProgress(40);
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(email)) return false;
    await new Promise(r => setTimeout(r, 800)); setVerificationProgress(70);
    const domain = email.split('@')[1];
    if (['temp.com', 'test.com'].includes(domain)) return false;
    await new Promise(r => setTimeout(r, 500)); setVerificationProgress(100);
    return true; 
  };

  const handleStartSubmission = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!reviewForm.name || !reviewForm.email || !reviewForm.comment) { setFormError('All fields required'); return; }
    const isValid = await verifyEmailRealTime(reviewForm.email);
    if (!isValid) { setFormError('Invalid or disposable email detected.'); setVerificationStep('form'); return; }
    setIsSubmitting(true);
    setVerificationStep('success');
    setIsSubmitting(false);
  };

  if (loading) return <div className="pt-32 pb-20 text-center"><Loader2 className="animate-spin inline mr-2"/> Synchronizing Portfolio Data...</div>;
  if (!property) return <div className="pt-32 pb-20 text-center">Property not found in database.</div>;

  const images = property.images || [property.imageUrl];
  const displayAgent = agent || { name: 'Guardian Admin', email: 'hello@guardianhousing.ae', phone: '+971505804669', photoUrl: 'https://images.unsplash.com/photo-1560250097-0b93528c311a' };

  return (
    <div className="pt-24 pb-20 min-h-screen bg-slate-50">
      
      {/* Premium Gallery Header */}
      <div className="container mx-auto px-6 mb-8">
          <div className="flex justify-between items-center mb-6 pt-4">
              <Link to="/buy" className="text-slate-900 font-bold flex items-center gap-2 hover:text-gold-500 transition-colors uppercase text-xs tracking-widest"><ArrowLeft size={16}/> Back to Portfolio</Link>
              <div className="flex items-center gap-2">
                 {property.referenceId && <span className="bg-slate-900 text-white text-[9px] px-3 py-1.5 rounded-full font-bold uppercase tracking-widest mr-2">REF: {property.referenceId}</span>}
                 <button onClick={() => setShowFullGallery(true)} className="flex items-center gap-2 bg-white text-slate-900 px-4 py-2 rounded shadow-sm border font-bold text-[10px] uppercase tracking-widest hover:bg-slate-50 transition-all"><Grid size={14}/> Full Gallery</button>
              </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-4 h-[500px] overflow-hidden rounded-lg shadow-xl relative">
              {property.isDistress && (
                 <div className="absolute top-4 left-4 z-10 bg-red-600 text-white px-4 py-2 font-bold uppercase tracking-widest text-[10px] shadow-2xl animate-pulse">Distress Deal</div>
              )}
              <div className="md:col-span-2 md:row-span-2 relative group cursor-pointer overflow-hidden" onClick={() => setShowFullGallery(true)}>
                  <img src={images[0]} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/5 group-hover:bg-black/20 transition-all"></div>
              </div>
              <div className="hidden md:block md:col-span-2 relative group cursor-pointer overflow-hidden" onClick={() => setShowFullGallery(true)}>
                  <img src={images[1] || images[0]} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="hidden md:block relative group cursor-pointer overflow-hidden" onClick={() => setShowFullGallery(true)}>
                  <img src={images[2] || images[0]} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="hidden md:block relative group cursor-pointer overflow-hidden" onClick={() => setShowFullGallery(true)}>
                  <img src={images[3] || images[0]} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white font-bold text-xl">+{images.length - 3}</div>
              </div>
          </div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
         <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-2/3">
               <div className="bg-white p-10 rounded shadow-lg border border-gray-100 mb-8 relative">
                   <div className="flex justify-between items-start mb-8 border-b pb-6">
                      <div>
                          <div className="flex items-center gap-2 mb-2">
                             <span className="bg-gold-500 text-white font-bold uppercase tracking-widest text-[9px] px-3 py-1 rounded-sm">{property.propertyType}</span>
                             {property.developer && <span className="bg-slate-100 text-slate-600 font-bold uppercase tracking-widest text-[9px] px-3 py-1 rounded-sm border border-slate-200">DEV: {property.developer}</span>}
                          </div>
                          <h1 className="text-3xl md:text-5xl font-serif text-slate-900 mt-1">{property.title}</h1>
                          {property.projectName && <p className="text-gold-600 font-bold text-sm uppercase tracking-widest mt-1">{property.projectName}</p>}
                          <div className="flex items-center text-gray-400 text-sm mt-3"><MapPin size={16} className="mr-2 text-gold-500"/> {property.location}</div>
                      </div>
                      <div className="text-right">
                          <div className="text-3xl md:text-4xl font-bold text-slate-900">{property.price.toLocaleString()} <span className="text-sm font-normal text-gray-400 uppercase">{property.currency}</span></div>
                          {property.isDistress && property.originalPrice && (
                             <p className="text-sm text-gray-400 line-through mt-1">AED {property.originalPrice.toLocaleString()}</p>
                          )}
                          <span className="text-[10px] font-bold text-green-600 uppercase tracking-[0.2em] bg-green-50 px-3 py-1 rounded mt-2 inline-block border border-green-100">{property.status}</span>
                      </div>
                   </div>

                   {/* Tech specs bar */}
                   <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-8 mb-10 bg-slate-50/50 px-6 rounded border border-slate-100">
                       <div className="flex flex-col items-center text-center">
                          <Bed size={22} className="text-gold-500 mb-2"/>
                          <span className="text-[9px] text-gray-400 uppercase font-bold tracking-[0.2em] mb-1">Beds</span>
                          <span className="font-bold text-slate-800 text-sm">{property.bedType}</span>
                       </div>
                       <div className="flex flex-col items-center text-center border-l border-slate-200">
                          <Bath size={22} className="text-gold-500 mb-2"/>
                          <span className="text-[9px] text-gray-400 uppercase font-bold tracking-[0.2em] mb-1">Baths</span>
                          <span className="font-bold text-slate-800 text-sm">{property.bathType}</span>
                       </div>
                       <div className="flex flex-col items-center text-center border-l border-slate-200">
                          <Square size={22} className="text-gold-500 mb-2"/>
                          <span className="text-[9px] text-gray-400 uppercase font-bold tracking-[0.2em] mb-1">Area</span>
                          <span className="font-bold text-slate-800 text-sm">{property.sqft.toLocaleString()} Sqft</span>
                       </div>
                       <div className="flex flex-col items-center text-center border-l border-slate-200">
                          <Layers size={22} className="text-gold-500 mb-2"/>
                          <span className="text-[9px] text-gray-400 uppercase font-bold tracking-[0.2em] mb-1">Metric</span>
                          <span className="font-bold text-slate-800 text-sm">{property.sizeSqm ? `${property.sizeSqm} m²` : '--'}</span>
                       </div>
                   </div>

                   {/* Specialized Info Grid */}
                   <div className="grid md:grid-cols-2 gap-10 mb-12">
                      <div className="space-y-4">
                         <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900 border-b pb-2">Financial Overview</h3>
                         <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                               <div className="p-2 bg-slate-100 rounded text-slate-600"><Wallet size={16}/></div>
                               <div>
                                  <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Schedule</p>
                                  <p className="text-xs font-bold text-slate-700">{property.rentalFreq || 'Annual'}</p>
                               </div>
                            </div>
                            <div className="flex items-center gap-3">
                               <div className="p-2 bg-slate-100 rounded text-slate-600"><RefreshCw size={16}/></div>
                               <div>
                                  <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Installments</p>
                                  <p className="text-xs font-bold text-slate-700">{property.cheques || '4'} Cheques</p>
                               </div>
                            </div>
                         </div>
                      </div>
                      <div className="space-y-4">
                         <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900 border-b pb-2">Extra Features</h3>
                         <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded text-slate-600"><Sparkles size={16}/></div>
                            <div>
                               <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Additional Space</p>
                               <p className="text-xs font-bold text-slate-700">{property.studyRoom ? 'Study Room Included' : 'No Study Room'}</p>
                            </div>
                         </div>
                      </div>
                   </div>

                   <h3 className="text-xl font-serif font-bold text-slate-900 mb-4 flex items-center gap-2"><ArrowLeft size={18} className="rotate-180 text-gold-500"/> Property Highlights</h3>
                   <div className="prose text-gray-600 leading-relaxed mb-8 whitespace-pre-line text-sm max-w-none">{property.description}</div>
               </div>

               {/* Reviews */}
               <div className="bg-white p-10 rounded shadow-lg border border-gray-100 mb-8">
                  <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-100">
                     <div className="flex items-center gap-3">
                        <Star className="text-gold-500" size={24}/>
                        <h3 className="text-2xl font-serif text-slate-900">Verified Client Experiences</h3>
                     </div>
                     <button onClick={() => setShowReviewModal(true)} className="bg-slate-900 text-white font-bold uppercase tracking-widest text-[9px] px-6 py-3 rounded shadow-lg hover:bg-gold-500 transition-all">Write Public Review</button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                      {reviews.map(rev => (
                        <div key={rev.id} className="p-6 bg-slate-50 rounded-sm border border-slate-100">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-bold text-xs flex items-center gap-2">{rev.userName} <ShieldCheck size={14} className="text-blue-500"/></h4>
                                <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} size={8} fill={i < rev.rating ? "#D4AF37" : "none"} className={i < rev.rating ? "text-gold-400" : "text-gray-200"} />)}</div>
                            </div>
                            <p className="text-[11px] text-gray-500 italic leading-relaxed">"{rev.comment}"</p>
                        </div>
                      ))}
                      {reviews.length === 0 && <div className="col-span-2 text-center text-gray-400 text-xs italic py-10 bg-slate-50/50 rounded border border-dashed border-slate-200">No verified reviews have been posted for this premium listing yet.</div>}
                  </div>
               </div>
            </div>

            <div className="lg:w-1/3">
                <div className="bg-white p-8 rounded shadow-lg border-t-8 border-gold-500 sticky top-32 shadow-gold-500/5">
                    <div className="flex items-center gap-5 mb-10 border-b pb-8">
                        <div className="relative">
                           <img src={displayAgent.photoUrl} className="w-24 h-24 rounded-full object-cover shadow-xl border-4 border-white"/>
                           <div className="absolute -bottom-1 -right-1 bg-green-500 w-5 h-5 rounded-full border-4 border-white"></div>
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold text-slate-900 leading-tight">{displayAgent.name}</h3>
                            <p className="text-[10px] text-gold-600 font-bold uppercase tracking-[0.2em] mt-1">Certified Senior Portfolio Advisor</p>
                            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">RERA Registered</p>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <a href={`tel:${displayAgent.phone}`} className="flex items-center justify-center gap-3 w-full py-5 bg-slate-900 text-white rounded font-bold uppercase tracking-widest text-[10px] hover:bg-gold-500 transition-all shadow-xl shadow-slate-200"><Phone size={16}/> Direct Priority Line</a>
                        <a href={`https://wa.me/${displayAgent.phone.replace(/\D/g, '')}`} className="flex items-center justify-center gap-3 w-full py-5 bg-[#25D366] text-white rounded font-bold uppercase tracking-widest text-[10px] hover:bg-[#128C7E] transition-all shadow-xl shadow-green-100"><MessageCircle size={16}/> WhatsApp Identity Verified</a>
                        <a href={`mailto:${displayAgent.email}`} className="flex items-center justify-center gap-3 w-full py-5 border border-slate-200 text-slate-700 rounded font-bold uppercase tracking-widest text-[10px] hover:bg-slate-50 transition-all"><Mail size={16}/> Secure Corporate Email</a>
                    </div>
                    
                    <div className="mt-10 p-4 bg-slate-50 rounded border border-slate-100">
                       <p className="text-[9px] text-gray-400 uppercase font-bold tracking-widest mb-2">Investment Support</p>
                       <p className="text-xs text-slate-600 leading-relaxed italic">Our agents provide full support from viewings to DLD transfer and post-handover property management.</p>
                    </div>
                </div>
            </div>
         </div>
      </div>

      {/* FULL GALLERY MODAL */}
      {showFullGallery && (
          <div className="fixed inset-0 z-[200] bg-black flex flex-col items-center animate-fade-in">
              <div className="w-full flex justify-between p-8 bg-black/80 text-white relative z-10 backdrop-blur-md">
                  <div>
                    <h4 className="font-serif text-2xl">{property.title}</h4>
                    <p className="text-gold-500 text-xs font-bold uppercase tracking-widest">{property.location}</p>
                  </div>
                  <button onClick={() => setShowFullGallery(false)} className="hover:text-gold-500 transition-all bg-white/10 p-2 rounded-full"><X size={32}/></button>
              </div>
              <div className="flex-grow flex items-center justify-center w-full px-4 relative">
                  <button onClick={() => setActiveImageIndex((activeImageIndex - 1 + images.length) % images.length)} className="absolute left-10 text-white/50 hover:text-gold-500 transition-all p-4 bg-white/5 rounded-full backdrop-blur-sm"><ChevronLeft size={48}/></button>
                  <img src={images[activeImageIndex]} className="max-w-full max-h-[85vh] object-contain shadow-2xl rounded-sm ring-1 ring-white/10" />
                  <button onClick={() => setActiveImageIndex((activeImageIndex + 1) % images.length)} className="absolute right-10 text-white/50 hover:text-gold-500 transition-all p-4 bg-white/5 rounded-full backdrop-blur-sm"><ChevronRight size={48}/></button>
              </div>
              <div className="w-full h-32 flex justify-center gap-3 p-4 bg-black/80 overflow-x-auto no-scrollbar backdrop-blur-md border-t border-white/5">
                  {images.map((img, idx) => (
                      <img key={idx} src={img} onClick={() => setActiveImageIndex(idx)} className={`h-full aspect-video object-cover rounded-sm cursor-pointer transition-all ${activeImageIndex === idx ? 'ring-2 ring-gold-500 scale-105 opacity-100' : 'opacity-30 hover:opacity-100'}`} />
                  ))}
              </div>
          </div>
      )}

      {/* REVIEW MODAL (Preserved with real-time logic) */}
      {showReviewModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fade-in">
              <div className="bg-white max-w-md w-full rounded shadow-2xl p-10 relative border-t-[12px] border-gold-500">
                  <button onClick={() => setShowReviewModal(false)} className="absolute top-6 right-6 text-gray-400 hover:text-slate-900 transition-colors"><X size={24}/></button>
                  {verificationStep === 'form' && (
                      <form onSubmit={handleStartSubmission} className="space-y-8">
                          <div className="text-center">
                             <h3 className="text-3xl font-serif text-slate-900 mb-2">Premium Client Review</h3>
                             <p className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Email delivery verification enabled</p>
                          </div>
                          <div className="flex justify-center gap-2 bg-slate-50 py-4 rounded">
                              {[1,2,3,4,5].map(n => <button type="button" key={n} onClick={()=>setReviewForm({...reviewForm, rating: n})} className="transition-transform hover:scale-110"><Star size={36} fill={n <= reviewForm.rating ? "#D4AF37" : "none"} className={n <= reviewForm.rating ? "text-gold-400" : "text-gray-100"}/></button>)}
                          </div>
                          <div className="space-y-4">
                            <input type="text" placeholder="Full Identity (Name)" value={reviewForm.name} onChange={e=>setReviewForm({...reviewForm, name: e.target.value})} className="w-full border p-4 rounded-sm text-sm outline-none focus:border-gold-500 bg-slate-50/50" />
                            <input type="email" placeholder="Professional Email Address" value={reviewForm.email} onChange={e=>setReviewForm({...reviewForm, email: e.target.value})} className="w-full border p-4 rounded-sm text-sm outline-none focus:border-gold-500 bg-slate-50/50" />
                            <textarea placeholder="Your comprehensive review about the property and service..." value={reviewForm.comment} onChange={e=>setReviewForm({...reviewForm, comment: e.target.value})} className="w-full border p-4 rounded-sm h-32 text-sm outline-none focus:border-gold-500 bg-slate-50/50 resize-none" />
                          </div>
                          {formError && <div className="flex items-center gap-2 text-red-600 text-[10px] font-bold uppercase tracking-tight bg-red-50 p-3 rounded border border-red-100"><AlertCircle size={14}/> {formError}</div>}
                          <button className="w-full bg-slate-900 text-white py-5 rounded-sm font-bold uppercase tracking-[0.2em] text-xs hover:bg-gold-500 transition-all shadow-2xl">Verify Identity & Post</button>
                      </form>
                  )}
                  {verificationStep === 'verifying' && (
                      <div className="py-24 text-center">
                          <div className="relative inline-block mb-10">
                             <div className="w-24 h-24 border-4 border-slate-100 rounded-full"></div>
                             <div className="absolute inset-0 flex items-center justify-center">
                                <Loader2 className="animate-spin text-gold-500" size={56}/>
                             </div>
                          </div>
                          <h4 className="text-2xl font-serif font-bold text-slate-900 mb-3">Global Mailbox Verification</h4>
                          <p className="text-xs text-gray-400 px-10 leading-relaxed uppercase tracking-[0.1em] font-bold">Synchronizing with MX records and DNS servers to authenticate identity...</p>
                          <div className="mt-12 bg-slate-100 h-2 w-full rounded-full overflow-hidden shadow-inner">
                              <div className="bg-gold-500 h-full transition-all duration-700 ease-out" style={{ width: `${verificationProgress}%` }}></div>
                          </div>
                          <p className="mt-4 text-[9px] font-bold text-gold-600 uppercase tracking-widest">{verificationProgress}% PROCESSED</p>
                      </div>
                  )}
                  {verificationStep === 'success' && (
                      <div className="py-24 text-center space-y-8">
                          <div className="w-24 h-24 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce border border-green-100 shadow-inner shadow-green-200">
                             <CheckCircle size={56}/>
                          </div>
                          <div className="space-y-2">
                            <h4 className="text-3xl font-serif font-bold text-slate-900">Experience Secured</h4>
                            <p className="text-gray-400 text-sm max-w-xs mx-auto">Your identity has been verified. The review will be visible once published by our portfolio managers.</p>
                          </div>
                          <button onClick={() => setShowReviewModal(false)} className="w-full bg-slate-900 text-white py-5 rounded-sm font-bold uppercase tracking-widest text-xs mt-8 shadow-2xl hover:bg-gold-500 transition-all">Return to Portfolio</button>
                      </div>
                  )}
              </div>
          </div>
      )}
    </div>
  );
};

export default PropertyDetails;
